import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, CircularProgress, Select, Menu, MenuItem, Autocomplete, TextField } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useToast } from 'use-toast-mui';
import QueryString from 'qs';
import ConfirmModal from '../../../common/modal/ConfirmModal';
import GroupChangeSummary from './GroupChangeSummary';
import { getUsersList } from '../../../../api/member-management';
import {
  searchTickets,
  getCoursePassSelectionOptions,
  getLockerPassSelectionOptions,
} from '../../../../api/ticket';
import { centersDetail } from '../../../../api/store-management';
import { addMemberGroupMembers, getMemberGroups, removeMemberGroupMembers } from '../../../../api/member-group';
import { errorMessages } from '../../../../utils/methods';
// 레이아웃은 문자 수신자 편집과 같은 디자인을 쓴다. 640줄짜리 styled 를 복제하지 않고 그대로 공유한다.
import {
  EditModalContainer,
  EditHeader,
  EditHeaderTitle,
  EditHeaderClose,
  EditBody,
  PoolPanel,
  PanelGap,
  PickedPanel,
  SearchRow,
  SearchField,
  FilterToggleButton,
  ResultRow,
  ResultLabel,
  AddAllButton,
  ListHead,
  ListScroll,
  PoolRow,
  PickedRow,
  RowCheck,
  Cell,
  HeadCell,
  NameCell,
  TypeBadge,
  RemoveRowButton,
  EmptyMessage,
  PickedHeader,
  PickedTitle,
  PickedSearchField,
  FilterPanel,
  FilterPanelHeader,
  FilterPanelTitle,
  FilterPanelClose,
  FilterPanelBody,
  FilterSection,
  FilterSectionTitle,
  ChipRow,
  FilterChip,
  FilterSectionHint,
  ProductMultiHint,
  ProductPickRow,
  ProductTagRow,
  ProductTag,
  ProductTagKind,
  ProductTagLabel,
  ProductTagRemove,
  ProductKindSelectSx,
  ProductInputSx,
  FilterPanelFooter,
  FilterResetButton,
  FilterApplyButton,
  UndoBar,
  UndoLabel,
  UndoAction,
  EditFooter,
  SummaryText,
  SummaryStrong,
  SummaryMuted,
  FooterActions,
  CancelButton,
  ApplyButton,
} from '../MemberRecipientPickerModalStyled';
import { NewMemberChip, RemoveMemberButtonSx } from './MemberGroupModalStyled';

// 조회 상한. 회원목록의 전체선택이 쓰는 값과 동일하게 맞춘다.
const FETCH_LIMIT = 10000;

// 한 번에 그리는 행 수. 수천 행을 통째로 그리면 스크롤이 끊긴다.
const RENDER_CHUNK = 120;

// useQuery data 기본값. 인라인 [] 리터럴은 렌더마다 새 참조가 되어 deps 로 쓴 effect 가 루프에 빠진다.
const EMPTY_POOL = [];

// useQuery data 기본값 — 렌더마다 새 참조가 되지 않게 상수로 둔다.
const EMPTY_TYPE_MAP = {};

const MEMBER_TYPE_CHIPS = [
  { value: null, label: '전체' },
  { value: 'ACTIVE', label: '유효회원' },
  { value: 'SCHEDULED', label: '사용예정' },
  { value: 'EXPIRING_SOON', label: '만료임박' },
  { value: 'STOPPED', label: '중지회원' },
  { value: 'UNPAID', label: '미수회원' },
  { value: 'EXPIRED', label: '만료회원' },
];

const TICKET_TYPE_CHIPS = [
  { value: null, label: '전체' },
  { value: 'COURSE_TICKET', label: '수강권' },
  { value: 'COURSE_PASS', label: '이용권' },
  { value: 'TRIAL_TICKET', label: '일일권' },
];

const GENDER_CHIPS = [
  { value: null, label: '전체' },
  { value: 'MALE', label: '남자' },
  { value: 'FEMALE', label: '여자' },
];

const SPORT_TYPE_LABELS = {
  FITNESS: '피트니스 / P.T',
  PILATES: '필라테스',
  BARRE_PILATES: '바레 필라테스',
  GOLF: '골프',
  YOGA: '요가',
  TANNING: '태닝',
  STRETCHING: '스트레칭',
};

const MEMBER_TYPE_TONE = {
  유효회원: '#00838F',
  사용예정: '#3564D6',
  만료임박: '#C46A00',
  중지회원: '#C2403D',
  미수회원: '#F49215',
  라커만료: '#8A939C',
  만료회원: '#8A939C',
};

const EMPTY_FILTERS = {
  memberType: null,
  exerciseType: null,
  ticketType: null,
  gender: null,
  // 상품 단위 필터 — 고른 상품 중 하나라도 가진 회원을 본다(복수 선택).
  products: [], // [{ kind, id, label }]
};

// 상품 종류. 개인·그룹은 같은 수강권 목록을 classType 으로 가른다.
const PRODUCT_KINDS = [
  { value: 'PRIVATE', label: '개인' },
  { value: 'GROUP', label: '그룹' },
  { value: 'PASS', label: '기간' },
  { value: 'LOCKER', label: '라커' },
];

const digitsOf = value => String(value ?? '').replace(/[^0-9]/g, '');

// 좌우 목록의 출처가 다르다. 회원목록 API는 '남성'/'여성' 한글을, 그룹 소속 회원 API는
// Gender enum(MALE/FEMALE)을 내려준다. 회원목록 화면 표기(남성/여성)로 맞춰 한 줄에 섞여도 같아 보이게 한다.
const genderLabel = value => {
  if (value === 'MALE' || value === '남성') return '남성';
  if (value === 'FEMALE' || value === '여성') return '여성';
  return '-';
};

// 좌측 회원목록과 우측 그룹 소속 회원은 서로 다른 API 응답이지만
// memberId·name·phoneNumber·gender 는 같은 키를 쓴다. 구분(memberType)은 회원목록에만 있다.
const normalize = raw => ({
  id: raw.memberId,
  raw,
  name: raw.name || '-',
  phone: raw.phoneNumber || '-',
  gender: genderLabel(raw.gender),
  typeLabel: raw.memberType || '',
});

const matchesKeyword = (row, keyword) => {
  if (!keyword) return true;
  const numeric = digitsOf(keyword);
  return row.name.includes(keyword) || (!!numeric && digitsOf(row.phone).includes(numeric));
};

/**
 * 그룹 소속 회원 편집 모달.
 * 왼쪽에서 조건으로 회원을 찾아 담고, 오른쪽에서 빼면서 소속을 편집한다.
 * 문자 수신자 편집과 같은 계약으로, '적용'을 눌러야 서버에 반영된다.
 */
/**
 * 그룹 소속 회원 편집 모달.
 *
 * <p>{@code onPick} 을 주면 <b>고르기 전용</b>으로 동작한다 — 서버에 저장하지 않고 고른 회원을
 * 그대로 돌려준다. 포인트 지급 대상 선택이 이 경로를 쓴다. 검색·상세필터·담기·되돌리기는
 * 그룹 편집과 완전히 같아야 해서 화면을 새로 만들지 않고 마지막 '적용'만 갈랐다.
 */
const MemberGroupMemberEditModal = ({
  open,
  onClose,
  group,
  currentMembers = [],
  onSaved,
  onPick,
  title,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const user = useSelector(state => state.user.user);

  // 담긴 회원. id → 원본 객체
  const [picked, setPicked] = useState(() => new Map());
  // 편집 시작 시점의 소속. 적용할 때 이것과 비교해 추가/제외를 가른다.
  const [baseIds, setBaseIds] = useState(() => new Set());
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);

  const [searchInput, setSearchInput] = useState('');
  const [keyword, setKeyword] = useState(''); // debounce 확정된 검색어
  const [filters, setFilters] = useState(EMPTY_FILTERS); // 조회에 반영된 필터
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS); // 패널에서 편집 중인 필터
  const [filterOpen, setFilterOpen] = useState(false);
  /*
   * 그룹 불러오기 (고르기 전용) — 문자 수신자 선택과 같다. 좌측 목록을 그 그룹 소속으로 좁힐 뿐
   * 담기는 기존 체크 흐름 그대로다. 검색어·필터와 함께 걸린다.
   */
  const [groupId, setGroupId] = useState(null);
  const [groupAnchor, setGroupAnchor] = useState(null);
  const [pickedKeyword, setPickedKeyword] = useState('');
  const [poolRenderCount, setPoolRenderCount] = useState(RENDER_CHUNK);
  const [pickedRenderCount, setPickedRenderCount] = useState(RENDER_CHUNK);

  // 좌측 목록의 체크 상태. 담기 버튼을 눌러야 우측으로 이동한다.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  // 드래그 다중 선택 — 시작 행의 목표 상태(선택/해제)를 지나가는 행에 그대로 적용
  const dragModeRef = useRef(null);

  // 열 때마다 현재 소속으로 초기화한다. 취소하면 아무것도 남지 않는다.
  useEffect(() => {
    if (!open) return;
    setPicked(new Map(currentMembers.map(m => [m.memberId, m])));
    setBaseIds(new Set(currentMembers.map(m => m.memberId)));
    setHistory([]);
    setSearchInput('');
    setKeyword('');
    setFilters(EMPTY_FILTERS);
    setDraftFilters(EMPTY_FILTERS);
    setFilterOpen(false);
    setGroupId(null);
    setGroupAnchor(null);
    setPickedKeyword('');
    setSelectedIds(new Set());
    // 열릴 때 한 번만 초기화한다. 편집 중 부모의 소속 목록이 갱신돼도 덮어쓰지 않는다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // 드래그 선택 종료 (마우스를 어디서 떼든)
  useEffect(() => {
    const stopDrag = () => {
      dragModeRef.current = null;
    };
    window.addEventListener('mouseup', stopDrag);
    return () => window.removeEventListener('mouseup', stopDrag);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setKeyword(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const branchId = user?.branch?.branchId;
  const headBranchId = user?.headBranch?.headBranchId;
  // 지점 소속 계정이 branchId 없이 조회하면 타 지점 회원까지 딸려온다. 목록 화면과 같은 가드.
  const canFetch = !!headBranchId && (user?.centerType !== 'BRANCH' || !!branchId);

  const { data: sportTypes = [] } = useQuery({
    queryKey: ['memberGroupEditSportTypes', user?.branch?.centerId],
    queryFn: async () => {
      const response = await centersDetail(user?.token, user?.branch?.centerId);
      return response?.branchDetailInfo?.sportTypes || [];
    },
    enabled: open && !!user?.token && !!user?.branch?.centerId,
  });

  /*
   * 상품 선택지. 종류마다 목록 API 가 다르다.
   * 개인·그룹은 같은 수강권 목록을 classType 으로 갈라 쓰고, 기간은 이용권(+PASS 일일권),
   * 라커는 지점 라커 상품이다. 패널을 열었을 때만 받는다.
   */
  // 종류는 목록을 고르는 도구일 뿐 조회 조건이 아니다. 그래서 필터가 아니라 화면 상태로 둔다.
  const [productKind, setProductKind] = useState('PRIVATE');

  const { data: courseTicketOptions = EMPTY_POOL } = useQuery({
    queryKey: ['editProductCourseTickets', branchId],
    queryFn: () => searchTickets(user.token, branchId),
    enabled: filterOpen && !!branchId && !!user?.token
      && (productKind === 'PRIVATE' || productKind === 'GROUP'),
  });

  const { data: coursePassOptions = EMPTY_POOL } = useQuery({
    queryKey: ['editProductCoursePasses', branchId],
    queryFn: () => getCoursePassSelectionOptions(user.token, branchId),
    enabled: filterOpen && !!branchId && !!user?.token && productKind === 'PASS',
  });

  const { data: lockerPassOptions = EMPTY_POOL } = useQuery({
    queryKey: ['editProductLockerPasses', branchId],
    queryFn: () => getLockerPassSelectionOptions(user.token, branchId),
    enabled: filterOpen && !!branchId && !!user?.token && productKind === 'LOCKER',
  });

  // 고른 종류에 맞는 선택지 한 벌 — {id, label}
  const productOptions = useMemo(() => {
    if (productKind === 'PRIVATE' || productKind === 'GROUP') {
      const wanted = productKind === 'PRIVATE' ? 'PRIVATE_LESSON' : 'GROUP_LESSON';
      return (courseTicketOptions || [])
        .filter(item => item.classType === wanted)
        .map(item => ({ id: item.courseTicketId, label: item.courseTicketName }));
    }
    if (productKind === 'PASS') {
      return (coursePassOptions || []).map(item => ({
        id: item.coursePassId,
        label: item.coursePassName,
      }));
    }
    return (lockerPassOptions || []).map(item => ({
      id: item.lockerPassId,
      label: item.lockerPassName,
    }));
  }, [productKind, courseTicketOptions, coursePassOptions, lockerPassOptions]);

  const poolQuery = useMemo(
    () => ({
      page: 1,
      limit: FETCH_LIMIT,
      headBranchId,
      branchId,
      searchInput: keyword,
      memberTypes: filters.memberType ? [filters.memberType] : [],
      exerciseTypes: filters.exerciseType ? [filters.exerciseType] : [],
      ticketTypes: filters.ticketType ? [filters.ticketType] : [],
      // 고른 상품을 종류별로 갈라 보낸다. 서버는 그중 하나라도 가진 회원을 준다.
      courseTicketIds: filters.products
        .filter(p => p.kind === 'PRIVATE' || p.kind === 'GROUP')
        .map(p => p.id),
      coursePassIds: filters.products.filter(p => p.kind === 'PASS').map(p => p.id),
      lockerPassIds: filters.products.filter(p => p.kind === 'LOCKER').map(p => p.id),
      genders: filters.gender ? [filters.gender] : [],
      memberGroupId: groupId || '',
      sortBy: 'name',
      sortDirection: 'ASC',
    }),
    [headBranchId, branchId, keyword, filters, groupId],
  );

  const { data: groups = EMPTY_POOL } = useQuery({
    queryKey: ['memberGroups', branchId],
    queryFn: () => getMemberGroups(user.token, branchId),
    enabled: open && !!onPick && !!branchId && !!user?.token,
  });

  const selectedGroup = groups.find(item => item.id === groupId) || null;

  const {
    data: poolRows = EMPTY_POOL,
    isFetching: poolFetching,
    isLoading: poolLoading,
    isError: poolError,
  } = useQuery({
    queryKey: ['memberGroupEditPool', poolQuery],
    queryFn: async () => {
      const result = await getUsersList(user.token, QueryString.stringify(poolQuery));
      return (result?.content || []).map(normalize);
    },
    enabled: open && !!user?.token && canFetch,
    // 편집 도중 포커스 복귀로 재조회되면 스크롤 위치와 렌더 청크가 되감긴다.
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    setPoolRenderCount(RENDER_CHUNK);
    setSelectedIds(new Set());
  }, [poolRows]);

  // 그룹 소속 조회는 회원 구분(memberType)을 주지 않는다. 표시용으로 회원목록을 그룹 필터로
  // 한 번 더 받아 memberId → 구분 사전을 만든다.
  const { data: typeMap = EMPTY_TYPE_MAP } = useQuery({
    queryKey: ['memberGroupEditMemberTypes', group?.id, branchId],
    queryFn: async () => {
      const queryString = QueryString.stringify({
        page: 1,
        limit: FETCH_LIMIT,
        headBranchId,
        branchId,
        memberGroupId: group.id,
      });
      const result = await getUsersList(user.token, queryString);
      return Object.fromEntries(
        (result?.content || []).map(row => [row.memberId, row.memberType]),
      );
    },
    enabled: open && !!group?.id && !!user?.token && canFetch,
  });

  const pickedRows = useMemo(() => [...picked.values()].map(normalize), [picked]);

  // 담긴 행은 두 출처가 섞인다. 회원목록에서 온 행은 자기 구분을 들고 있고,
  // 기존 소속 행은 위에서 만든 사전에서 찾는다.
  const typeOf = row => row.typeLabel || typeMap[row.id] || '';

  const visiblePickedRows = useMemo(() => {
    const trimmed = pickedKeyword.trim();
    return pickedRows.filter(row => matchesKeyword(row, trimmed));
  }, [pickedRows, pickedKeyword]);

  useEffect(() => setPickedRenderCount(RENDER_CHUNK), [pickedKeyword]);

  const unpickedPoolRows = useMemo(() => poolRows.filter(row => !picked.has(row.id)), [poolRows, picked]);

  const addedIds = useMemo(() => pickedRows.filter(row => !baseIds.has(row.id)).map(row => row.id), [
    pickedRows,
    baseIds,
  ]);
  const removedIds = useMemo(() => [...baseIds].filter(id => !picked.has(id)), [baseIds, picked]);

  // ---- 편집 ----
  const applyEdit = (kind, rows, label) => {
    if (!rows.length) return;
    setPicked(prev => {
      const next = new Map(prev);
      rows.forEach(row => (kind === 'add' ? next.set(row.id, row.raw) : next.delete(row.id)));
      return next;
    });
    setHistory(prev => [...prev, { kind, rows, label }].slice(-30));
  };

  const undo = () => {
    const last = history[history.length - 1];
    if (!last) return;
    setPicked(prev => {
      const next = new Map(prev);
      last.rows.forEach(row => (last.kind === 'add' ? next.delete(row.id) : next.set(row.id, row.raw)));
      return next;
    });
    setHistory(prev => prev.slice(0, -1));
  };

  const setRowSelected = (rowId, on) =>
    setSelectedIds(prev => {
      if (prev.has(rowId) === on) return prev;
      const next = new Set(prev);
      if (on) next.add(rowId);
      else next.delete(rowId);
      return next;
    });

  const handleRowMouseDown = row => {
    const willSelect = !selectedIds.has(row.id);
    dragModeRef.current = willSelect;
    setRowSelected(row.id, willSelect);
  };

  const handleRowMouseEnter = row => {
    if (dragModeRef.current === null) return;
    setRowSelected(row.id, dragModeRef.current);
  };

  const toggleAllPoolRows = () => {
    if (!unpickedPoolRows.length) return;
    const allSelected = unpickedPoolRows.every(row => selectedIds.has(row.id));
    setSelectedIds(allSelected ? new Set() : new Set(unpickedPoolRows.map(row => row.id)));
  };

  const addSelectedRows = () => {
    const rows = unpickedPoolRows.filter(row => selectedIds.has(row.id));
    if (!rows.length) return;
    applyEdit('add', rows, `${t('선택한')} ${rows.length}${t('명을 추가했습니다')}`);
    setSelectedIds(new Set());
  };

  // ---- 필터 패널 ----
  // productKind 는 기본값이 있어 그냥 세면 늘 1이 더 잡힌다. 실제로 고른 조건만 센다.
  const activeFilterCount =
    Object.entries(filters).filter(([key, value]) => key !== 'products' && value !== null).length
    + (filters.products.length ? 1 : 0);

  const openFilterPanel = () => {
    setDraftFilters(filters);
    setFilterOpen(true);
  };

  const pickDraft = (key, value) => setDraftFilters(prev => ({ ...prev, [key]: value }));

  const renderChips = (key, chips) => (
    <ChipRow>
      {chips.map(chip => (
        <FilterChip
          key={chip.value === null ? 'all' : String(chip.value)}
          active={draftFilters[key] === chip.value}
          onClick={() => pickDraft(key, chip.value)}>
          {t(chip.label)}
        </FilterChip>
      ))}
    </ChipRow>
  );

  const handleScroll = (event, total, count, setCount) => {
    if (count >= total) return;
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 200) setCount(prev => prev + RENDER_CHUNK);
  };

  // ---- 저장 ----
  // 추가/제외를 순차로 보낸다. 앞이 성공하고 뒤가 실패할 수 있으므로 단계마다 기준 소속을 옮겨,
  // 모달을 열어 둔 채 다시 누르면 이미 커밋된 요청은 다시 나가지 않게 한다.
  /*
   * 담기·빼기를 한 번에 커밋하는 자리라, 누르기 전에 인원이 어떻게 바뀌는지 보여준다 (#78).
   * 되돌리려면 다시 편집해야 하므로 숫자만 던지지 않고 전후를 나란히 둔다.
   */
  const [confirmOpen, setConfirmOpen] = useState(false);

  const askApply = () => {
    if (saving) return;
    // 고르기 전용이거나 바뀐 게 없으면 물을 것이 없다.
    if (onPick || (!addedIds.length && !removedIds.length)) {
      handleApply();
      return;
    }
    setConfirmOpen(true);
  };

  const handleApply = async () => {
    if (saving) return;
    // 고르기 전용: 서버에 쓰지 않고 지금 담긴 회원을 그대로 돌려준다.
    if (onPick) {
      onPick([...picked.values()]);
      onClose();
      return;
    }
    if (!addedIds.length && !removedIds.length) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      const parts = [];
      if (addedIds.length) {
        const result = await addMemberGroupMembers(user.token, group.id, addedIds);
        setBaseIds(prev => new Set([...prev, ...addedIds]));
        // 기소속·미존재는 서버가 skip 으로 집계한다. 화면 기준이 아니라 서버가 실제로 넣은 수를 알린다.
        const added = result?.addedCount ?? addedIds.length;
        const skipped = result?.skippedCount ?? 0;
        parts.push(
          skipped > 0
            ? `${added}${t('명 추가 (이미 소속된')} ${skipped}${t('명 제외)')}`
            : `${added}${t('명 추가')}`,
        );
      }
      if (removedIds.length) {
        await removeMemberGroupMembers(user.token, group.id, removedIds);
        setBaseIds(prev => {
          const next = new Set(prev);
          removedIds.forEach(id => next.delete(id));
          return next;
        });
        parts.push(`${removedIds.length}${t('명 제외')}`);
      }
      toast.success(parts.join(', '));
      onClose();
    } catch (error) {
      toast.error(errorMessages(error));
    } finally {
      setSaving(false);
      // 앞 단계만 커밋된 경우에도 부모 목록·회원수를 다시 읽게 한다.
      onSaved?.();
    }
  };

  const selectedVisibleCount = unpickedPoolRows.filter(row => selectedIds.has(row.id)).length;
  const lastHistory = history[history.length - 1];

  return (
    <>
    <Modal
      open={open}
      /*
       * 배경 클릭·ESC 로 그냥 닫으면 담아 둔 추가/제외가 통째로 사라진다. 적용 전이라
       * 서버에는 아무것도 안 남아 되돌릴 수도 없다. 변경이 있으면 닫기를 막고 알린다.
       */
      onClose={(event, reason) => {
        if (saving) return;
        if ((addedIds.length || removedIds.length)
          && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
          toast.info(t('담아 둔 변경이 있습니다. 적용하거나 취소를 눌러 주세요'));
          return;
        }
        onClose();
      }}>
      <EditModalContainer>
        <EditHeader>
          <EditHeaderTitle>{title || `${group?.name || ''} · ${t('회원 편집')}`}</EditHeaderTitle>
          <EditHeaderClose onClick={onClose}>✕</EditHeaderClose>
        </EditHeader>

        <EditBody>
          {/* ---------------- 왼쪽: 회원 찾기 ---------------- */}
          <PoolPanel>
            <SearchRow>
              <SearchField>
                <span style={{ fontSize: '14px', color: '#a6a6a6' }}>🔍</span>
                <input
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  placeholder={t('이름 또는 전화번호를 입력해 주세요')}
                />
              </SearchField>
              <FilterToggleButton active={activeFilterCount > 0} onClick={openFilterPanel}>
                <FilterListIcon sx={{ fontSize: 14 }} />
                {activeFilterCount > 0 ? `${t('필터')} ${activeFilterCount}${t('개 적용중')}` : t('필터 검색')}
              </FilterToggleButton>
            </SearchRow>

            <ResultRow>
              <ResultLabel>
                {poolFetching
                  ? t('조회 중...')
                  : poolError
                    ? t('조회에 실패했습니다')
                    : `${t('조건 결과')} ${poolRows.length}${t('명')}`}
              </ResultLabel>
              <div style={{ flex: 1 }} />
              {onPick && (
                <AddAllButton
                  onClick={e => setGroupAnchor(e.currentTarget)}
                  sx={{
                    backgroundColor: groupId ? '#E4F5F6' : '#fff',
                    color: groupId ? '#0F7F85' : '#4B5563',
                    border: `1px solid ${groupId ? '#4AB3BC' : '#DDE2E5'}`,
                    marginRight: '6px',
                    transition: 'background-color 150ms ease, border-color 150ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)',
                    '&:hover': {
                      backgroundColor: groupId ? '#d5eff0' : 'rgba(74, 179, 188, 0.08)',
                      borderColor: '#4AB3BC',
                    },
                    '&:active': {
                      transform: 'scale(0.97)',
                    },
                  }}>
                  <GroupsOutlinedIcon sx={{ fontSize: 15, marginRight: '4px' }} />
                  {selectedGroup ? selectedGroup.name : t('그룹 불러오기')}
                </AddAllButton>
              )}
              <AddAllButton disabled={selectedVisibleCount === 0} onClick={addSelectedRows}>
                {`${t('선택')} ${selectedVisibleCount}${t('명 추가')}`}
              </AddAllButton>
            </ResultRow>

            {/* 그룹 선택 — 고르면 좌측 목록이 그 그룹 소속으로 좁혀진다. */}
            <Menu
              open={!!groupAnchor}
              anchorEl={groupAnchor}
              onClose={() => setGroupAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{
                paper: {
                  sx: {
                    marginTop: '4px',
                    minWidth: '220px',
                    maxHeight: '320px',
                    borderRadius: '10px',
                    border: '1px solid #E4E8EB',
                    boxShadow: '0 10px 28px rgba(0,0,0,0.14)',
                  },
                },
              }}>
              <MenuItem
                selected={!groupId}
                onClick={() => {
                  setGroupId(null);
                  setGroupAnchor(null);
                }}
                sx={{ fontSize: '13px' }}>
                {t('전체 회원')}
              </MenuItem>
              {groups.length === 0 ? (
                <MenuItem disabled sx={{ fontSize: '12.5px' }}>
                  {t('만들어 둔 그룹이 없습니다')}
                </MenuItem>
              ) : (
                groups.map(item => (
                  <MenuItem
                    key={item.id}
                    selected={groupId === item.id}
                    onClick={() => {
                      setGroupId(item.id);
                      setGroupAnchor(null);
                    }}
                    sx={{ fontSize: '13px', display: 'flex', gap: '8px' }}>
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.name}
                    </span>
                    <span style={{ flexShrink: 0, fontSize: '11.5px', color: '#8A939C' }}>
                      {item.memberCount}
                      {t('명')}
                    </span>
                  </MenuItem>
                ))
              )}
            </Menu>

            <ListHead>
              <RowCheck
                checked={unpickedPoolRows.length > 0 && selectedVisibleCount === unpickedPoolRows.length}
                onClick={toggleAllPoolRows}
                sx={{ cursor: 'pointer' }}>
                {unpickedPoolRows.length > 0 && selectedVisibleCount === unpickedPoolRows.length ? '✓' : ''}
              </RowCheck>
              <HeadCell w="72px">{t('구분')}</HeadCell>
              <HeadCell w="80px">{t('회원명')}</HeadCell>
              <HeadCell w="44px">{t('성별')}</HeadCell>
              <HeadCell grow>{t('전화번호')}</HeadCell>
            </ListHead>

            <ListScroll
              onScroll={e => handleScroll(e, unpickedPoolRows.length, poolRenderCount, setPoolRenderCount)}>
              {poolLoading ? (
                <EmptyMessage>
                  <CircularProgress size={20} sx={{ color: '#4ab3bc' }} />
                </EmptyMessage>
              ) : poolRows.length === 0 ? (
                <EmptyMessage>{t('조건에 맞는 회원이 없습니다')}</EmptyMessage>
              ) : unpickedPoolRows.length === 0 ? (
                <EmptyMessage>{t('조건 결과가 모두 추가됐습니다')}</EmptyMessage>
              ) : (
                unpickedPoolRows.slice(0, poolRenderCount).map((row, index) => {
                  const isSelected = selectedIds.has(row.id);
                  return (
                    <PoolRow
                      key={row.id}
                      odd={index % 2 === 1}
                      onMouseDown={() => handleRowMouseDown(row)}
                      onMouseEnter={() => handleRowMouseEnter(row)}
                      style={{ userSelect: 'none' }}>
                      <RowCheck checked={isSelected}>{isSelected ? '✓' : ''}</RowCheck>
                      <Cell w="72px" sx={{ display: 'flex', justifyContent: 'center' }}>
                        {row.typeLabel ? (
                          <TypeBadge tone={MEMBER_TYPE_TONE[row.typeLabel] || '#8A939C'}>
                            {row.typeLabel}
                          </TypeBadge>
                        ) : (
                          '-'
                        )}
                      </Cell>
                      <NameCell w="80px" center>{row.name}</NameCell>
                      <Cell w="44px" center>{row.gender}</Cell>
                      <Cell grow center>{row.phone}</Cell>
                    </PoolRow>
                  );
                })
              )}
            </ListScroll>
          </PoolPanel>

          <PanelGap />

          {/* ---------------- 오른쪽: 그룹 소속 회원 ---------------- */}
          <PickedPanel>
            <PickedHeader>
              <PickedTitle>
                {t('소속 회원')} <span>{`${pickedRows.length}${t('명')}`}</span>
              </PickedTitle>
              <PickedSearchField>
                <span style={{ fontSize: '12px', color: '#a6a6a6' }}>🔍</span>
                <input
                  value={pickedKeyword}
                  onChange={e => setPickedKeyword(e.target.value)}
                  placeholder={t('추가된 회원에서 찾기')}
                />
              </PickedSearchField>
            </PickedHeader>

            <ListHead sx={{ padding: '6px 20px' }}>
              <HeadCell w="72px">{t('구분')}</HeadCell>
              <HeadCell w="96px">{t('회원명')}</HeadCell>
              <HeadCell w="56px">{t('성별')}</HeadCell>
              <HeadCell grow>{t('전화번호')}</HeadCell>
              <HeadCell w="52px" />
              <HeadCell w="22px" />
            </ListHead>

            <ListScroll
              onScroll={e => handleScroll(e, visiblePickedRows.length, pickedRenderCount, setPickedRenderCount)}>
              {pickedRows.length === 0 ? (
                <EmptyMessage>{t('왼쪽에서 회원을 검색해 추가해 주세요')}</EmptyMessage>
              ) : visiblePickedRows.length === 0 ? (
                <EmptyMessage>{t('검색과 일치하는 회원이 없습니다')}</EmptyMessage>
              ) : (
                visiblePickedRows.slice(0, pickedRenderCount).map((row, index) => (
                  <PickedRow key={row.id} odd={index % 2 === 1}>
                    <Cell w="72px" sx={{ display: 'flex', justifyContent: 'center' }}>
                      {typeOf(row) ? (
                        <TypeBadge tone={MEMBER_TYPE_TONE[typeOf(row)] || '#8A939C'}>
                          {typeOf(row)}
                        </TypeBadge>
                      ) : (
                        '-'
                      )}
                    </Cell>
                    <NameCell w="96px" center>{row.name}</NameCell>
                    <Cell w="56px" center>{row.gender}</Cell>
                    <Cell grow center>{row.phone}</Cell>
                    <Cell w="52px" center>
                      {!baseIds.has(row.id) && <NewMemberChip>{t('신규')}</NewMemberChip>}
                    </Cell>
                    <RemoveRowButton
                      sx={RemoveMemberButtonSx}
                      onClick={() => applyEdit('remove', [row], `${row.name} ${t('님을 제외했습니다')}`)}>
                      ✕
                    </RemoveRowButton>
                  </PickedRow>
                ))
              )}
            </ListScroll>
          </PickedPanel>

          {/* ---------------- 필터 오버레이 ---------------- */}
          {filterOpen && (
            <FilterPanel>
              <FilterPanelHeader>
                <FilterPanelTitle>{t('조건으로 회원 찾기')}</FilterPanelTitle>
                <FilterPanelClose onClick={() => setFilterOpen(false)}>✕</FilterPanelClose>
              </FilterPanelHeader>
              <FilterPanelBody>
                {/* 상품이 가장 강한 조건이라 맨 위에 둔다. 종류를 고르고 그 옆에서 상품을 고른다. */}
                <FilterSection>
                  {/* 규칙을 한 번에 다 적으면 문장이 길어 안 읽힌다. 기본 뜻만 두고
                      복수 규칙은 실제로 여러 개를 골랐을 때만 아래에 덧붙인다. */}
                  <FilterSectionTitle>
                    {t('상품')}
                    <FilterSectionHint>{t('이 상품을 가진 회원만 보기')}</FilterSectionHint>
                  </FilterSectionTitle>
                  <ProductPickRow>
                    <Select
                      size="small"
                      value={productKind}
                      onChange={e => setProductKind(e.target.value)}
                      sx={ProductKindSelectSx}>
                      {PRODUCT_KINDS.map(kind => (
                        <MenuItem key={kind.value} value={kind.value} sx={{ fontSize: '13px' }}>
                          {t(kind.label)}
                        </MenuItem>
                      ))}
                    </Select>
                    <Autocomplete
                      size="small"
                      sx={{ flex: 1, minWidth: 0 }}
                      options={productOptions}
                      getOptionLabel={option => option.label || '-'}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      // 고르면 태그로 내려보내고 입력창은 비운다. 연달아 여러 개를 고를 수 있다.
                      value={null}
                      blurOnSelect
                      onChange={(event, option) => {
                        if (!option) return;
                        setDraftFilters(prev =>
                          prev.products.some(p => p.kind === productKind && p.id === option.id)
                            ? prev
                            : {
                                ...prev,
                                products: [
                                  ...prev.products,
                                  { kind: productKind, id: option.id, label: option.label },
                                ],
                              },
                        );
                      }}
                      noOptionsText={t('이 지점에 등록된 상품이 없습니다')}
                      renderInput={params => (
                        <TextField {...params} placeholder={t('상품 선택')} sx={ProductInputSx} />
                      )}
                    />
                  </ProductPickRow>
                  {draftFilters.products.length > 1 && (
                    <ProductMultiHint>
                      {t('고른 상품 중 하나라도 가진 회원이 나옵니다')}
                    </ProductMultiHint>
                  )}
                  {draftFilters.products.length > 0 && (
                    <ProductTagRow>
                      {draftFilters.products.map(product => (
                        <ProductTag key={`${product.kind}-${product.id}`}>
                          <ProductTagKind>
                            {PRODUCT_KINDS.find(k => k.value === product.kind)?.label}
                          </ProductTagKind>
                          <ProductTagLabel>{product.label}</ProductTagLabel>
                          <ProductTagRemove
                            title={t('선택 취소')}
                            onClick={() =>
                              setDraftFilters(prev => ({
                                ...prev,
                                products: prev.products.filter(
                                  p => !(p.kind === product.kind && p.id === product.id),
                                ),
                              }))
                            }>
                            ✕
                          </ProductTagRemove>
                        </ProductTag>
                      ))}
                    </ProductTagRow>
                  )}
                </FilterSection>
                <FilterSection>
                  <FilterSectionTitle>{t('회원 구분')}</FilterSectionTitle>
                  {renderChips('memberType', MEMBER_TYPE_CHIPS)}
                </FilterSection>
                <FilterSection>
                  <FilterSectionTitle>{t('운동 종목')}</FilterSectionTitle>
                  {renderChips('exerciseType', [
                    { value: null, label: '전체' },
                    ...sportTypes.map(type => ({ value: type, label: SPORT_TYPE_LABELS[type] || type })),
                  ])}
                </FilterSection>
                <FilterSection>
                  <FilterSectionTitle>{t('회원권 유형')}</FilterSectionTitle>
                  {renderChips('ticketType', TICKET_TYPE_CHIPS)}
                </FilterSection>
                <FilterSection>
                  <FilterSectionTitle>{t('성별')}</FilterSectionTitle>
                  {renderChips('gender', GENDER_CHIPS)}
                </FilterSection>

              </FilterPanelBody>
              <FilterPanelFooter>
                <FilterResetButton onClick={() => setDraftFilters(EMPTY_FILTERS)}>
                  {t('초기화')}
                </FilterResetButton>
                <FilterApplyButton
                  onClick={() => {
                    setFilters(draftFilters);
                    setFilterOpen(false);
                  }}>
                  {t('설정한 조건으로 회원검색')}
                </FilterApplyButton>
              </FilterPanelFooter>
            </FilterPanel>
          )}
        </EditBody>

        {lastHistory && (
          <UndoBar>
            <UndoLabel>{lastHistory.label}</UndoLabel>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <UndoAction onClick={undo}>
                {history.length > 1 ? `${t('되돌리기')} (${history.length})` : t('되돌리기')}
              </UndoAction>
              {/* 되돌리지 않고 최근 이력 1개만 걷어낸다 */}
              <UndoAction onClick={() => setHistory(prev => prev.slice(0, -1))} style={{ opacity: 0.75 }}>
                ✕
              </UndoAction>
            </div>
          </UndoBar>
        )}

        <EditFooter>
          <SummaryText>
            <SummaryStrong>{`${t('소속')} ${pickedRows.length}${t('명')}`}</SummaryStrong>
            <span>·</span>
            <SummaryMuted>{`${t('추가')} ${addedIds.length} / ${t('제외')} ${removedIds.length}`}</SummaryMuted>
          </SummaryText>
          <FooterActions>
            <CancelButton onClick={onClose}>{t('취소')}</CancelButton>
            {/* ApplyButton 은 Box 기반이라 disabled 속성이 DOM 으로 새어 나간다. 시각·클릭만 막는다. */}
            <ApplyButton onClick={askApply} sx={saving ? { opacity: 0.6, pointerEvents: 'none' } : null}>
              {saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : t('적용')}
            </ApplyButton>
          </FooterActions>
        </EditFooter>
      </EditModalContainer>

    </Modal>

    <ConfirmModal
      open={confirmOpen}
      onClose={() => !saving && setConfirmOpen(false)}
      onConfirm={() => {
        if (saving) return;
        setConfirmOpen(false);
        handleApply();
      }}
      message={`'${group?.name ?? ''}' 그룹의 회원을 이렇게 바꿉니다.`}
      highlightText={group?.name ?? ''}
      highlightColor="#0F7F85"
      cancelText="취소"
      confirmText="적용"
      confirmColor="#0F7F85"
      detail={(
        <GroupChangeSummary
          rows={[
            {
              label: '그룹 인원',
              before: `${baseIds.size}명`,
              after: `${baseIds.size + addedIds.length - removedIds.length}명`,
              tone: addedIds.length - removedIds.length > 0 ? 'up'
                : addedIds.length - removedIds.length < 0 ? 'down' : undefined,
            },
            ...(addedIds.length
              ? [{ label: '담기', before: '-', after: `${addedIds.length}명`, tone: 'up' }] : []),
            ...(removedIds.length
              ? [{ label: '빼기', before: '-', after: `${removedIds.length}명`, tone: 'down' }] : []),
          ]}
        />
      )}
    />
    </>
  );
};

export default MemberGroupMemberEditModal;
